import { AppStoreType, MobileRedirectParams } from 'types/mobile-redirects';
import { FundriseRouteConfig, RouteLocationNormalized } from 'vue-router';
import { appStoreVisitRedirect } from '@api/mobile-redirect';
import { flattenQueryParam } from '@utils/request';
import { isIphone } from '@utils/client';
import { logError } from '@utils/error-tracking';

/**
 * Handles mobile app store redirect logic with error fallback
 */
const handleAppStoreRedirect = async (
	to: RouteLocationNormalized,
	appType: AppStoreType,
	fallbackUrl: string
): Promise<void> => {
	const params: MobileRedirectParams = {
		utmCampaign: flattenQueryParam(to.query.utm_campaign) ?? null,
		utmContent: flattenQueryParam(to.query.utm_content) ?? null,
		utmMedium: flattenQueryParam(to.query.utm_medium) ?? null,
		utmSource: flattenQueryParam(to.query.utm_source) ?? null,
		distinctId: flattenQueryParam(to.query.distinct_id) ?? null,
		clickId: flattenQueryParam(to.query.clickid) ?? null,
		appType
	};

	try {
		const mobileRedirectLink = await appStoreVisitRedirect(params, { suppressToast: () => true });
		window.location.href = mobileRedirectLink.appRedirect;
	} catch (error) {
		logError(`Failed to get ${appType.toLowerCase().replace('_', ' ')} redirect URL`);
		console.error(error);
		window.location.href = fallbackUrl;
	}
};

const mobileRedirectRoutes: Array<FundriseRouteConfig> = [
	{
		path: '/play-store-redirect',
		name: 'play-store-redirect',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		beforeEnter: async (to) => {
			await handleAppStoreRedirect(
				to,
				'GOOGLE_PLAY',
				'https://play.google.com/store/apps/details?id=com.fundrise.android'
			);
		},
		// revisit using redirect
		component: () => import('@views/marketing-site/home-view.vue')
	},
	{
		path: '/app-store-redirect',
		name: 'app-store-redirect',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		beforeEnter: async (to) => {
			await handleAppStoreRedirect(
				to,
				'APPLE_APP_STORE',
				'https://apps.apple.com/us/app/fundrise-invest-in-alts/id1435387568'
			);
		},
		// revisit using redirect
		component: () => import('@views/marketing-site/home-view.vue')
	},
	{
		path: '/invest-now',
		name: 'invest-now',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		redirect: (to) => {
			if (isIphone()) {
				return { name: 'app-store-redirect', query: to.query };
			} else {
				return { name: 'start-investing', query: to.query };
			}
		}
	}
];

export default mobileRedirectRoutes;
